import { ArrowLeft, RefreshCw, Search } from "lucide-react";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../../components/ui/sheet";
import TransactionDetails from "./components/TransactionDetails";
import { useTransactionTableController } from "./useTransactionTableController";
import { BasicTable } from "../../../../components/BasicTable";
import { Transaction } from "../../../../../app/entities/Transaction";
import { ModalEditTransaction } from "./components/TransactionDetails/components/EditTransactionModal";
import { useEffect, useState } from "react";
import { TransactionSupport } from "./components/TransactionDetails/components/TransactionSupport";
import { UploadFiles } from "./components/TransactionDetails/components/UploadFiles";
import { MobileTransactionList } from "./components/MobileTransactionList";

interface TransactionTableProps {
  selectedItem: Transaction | null;
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
}
export const TransactionSheet = ({
  selectedItem,
  setSheetOpen,
  sheetOpen,
}: TransactionTableProps) => {
  const [step, setStep] = useState(0);

  const title = {
    0: "Detalhes da transação",
    1: "Solicitar informações",
    2: "Documentos",
  };

  useEffect(() => {
    if (sheetOpen) {
      setStep(0);
    }
  }, [sheetOpen]);

  const content = {
    0: (
      <TransactionDetails
        onSupport={() => setStep(1)}
        onDocuments={() => setStep(2)}
      />
    ),
    1: <TransactionSupport transaction={selectedItem} />,
    2: <UploadFiles data={selectedItem} onBack={() => setStep(0)} />,
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetContent className="w-[340px] overflow-y-auto rounded-2xl bg-[#F4F5F7] p-2 xs:mr-5 xs:mt-5 xs:h-[95%] xs:w-96 xs:p-5">
        <div className="flex h-full min-h-[800px] flex-col">
          <SheetHeader>
            <SheetTitle className="text-xl font-semibold">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {step > 0 && (
                    <ArrowLeft size={20} onClick={() => setStep(0)} />
                  )}
                  <h2>{title[step as keyof typeof title] as string}</h2>
                </div>
                {step === 0 && selectedItem && (
                  <ModalEditTransaction selectedTransaction={selectedItem} />
                )}
              </div>
              <div className="my-5 w-full border-b-[1px] border-gray01" />
            </SheetTitle>
          </SheetHeader>

          {selectedItem && content[step as keyof typeof content]}
        </div>
      </SheetContent>
    </Sheet>
  );
};
export const TransactionTable = () => {
  const {
    selectedItem,
    table,
    handleRowClick,
    handleSearchInputChange,
    handleSheetState,
    handleRefresh,
    sheetOpen,
    setSheetOpen,
    isLoading,
    isFetching,
    isFetchingNextPage,
    observerRef,
    shouldShowInfiniteScroll,
    transactions,
    fetchNextPage,
    isSearchActive,
    searchRange,
    searchFullPeriod,
    isFullPeriodSearch,
    productsCatalog,
  } = useTransactionTableController();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex items-center">
          <Search size={20} className="z-2 absolute left-4 top-3" />
          <Input
            className="ml-1 w-48 rounded-3xl pl-10 sm:w-64 md:w-80 lg:w-96"
            placeholder="Pesquisar"
            onChange={handleSearchInputChange}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={isFetching}
          className="rounded-lg"
          aria-label="Atualizar transações"
        >
          <RefreshCw
            size={18}
            className={isFetching ? "animate-spin" : undefined}
          />
        </Button>
      </div>

      <TransactionSheet
        selectedItem={selectedItem}
        setSheetOpen={setSheetOpen}
        sheetOpen={sheetOpen}
      />

      <>
        <div className="block md:hidden">
          <MobileTransactionList
            transactions={transactions}
            onClick={(item) => {
              handleRowClick(item);
              handleSheetState();
            }}
            onLoadMore={fetchNextPage}
            isLoadingMore={isFetchingNextPage}
            hasMore={shouldShowInfiniteScroll}
            products={productsCatalog}
          />

          {isSearchActive && !isFullPeriodSearch && !isFetching && (
            <div className="mt-6 flex flex-col items-center">
              <p className="text-center text-sm text-gray-700">
                Pesquisando propostas até{" "}
                <strong>
                  {searchRange.startDate.toLocaleDateString("pt-BR")}.
                </strong>
              </p>

              <Button
                variant="link"
                className="h-auto p-0 text-sm font-semibold"
                onClick={searchFullPeriod}
              >
                Clique aqui para pesquisar todo o período
              </Button>
            </div>
          )}
        </div>

        <div className="hidden md:block">
          <BasicTable<Transaction>
            table={table}
            handleRowClick={handleRowClick}
            handleSheetState={handleSheetState}
            type="only header"
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            observerRef={observerRef}
            shouldShowInfiniteScroll={shouldShowInfiniteScroll}
          />

          {isSearchActive && !isFullPeriodSearch && !isFetching && (
            <div className="mt-6 flex flex-col items-center">
              <p className="text-center text-sm text-gray-700">
                Pesquisando propostas até{" "}
                <strong>
                  {searchRange.startDate.toLocaleDateString("pt-BR")}.
                </strong>
              </p>

              <Button
                variant="link"
                className="h-auto p-0 text-sm font-semibold"
                onClick={searchFullPeriod}
              >
                Clique aqui para pesquisar todo o período
              </Button>
            </div>
          )}
        </div>
      </>
    </div>
  );
};
